import { useNavigate, useSearch } from "@tanstack/react-router";

import { FilterBar } from "@/components/data";
import type { FilterSpec } from "@/components/data";
import { applyFilters, parseFilters, type FilterValues } from "@/lib/filters";

import type { FormatOption, RunOption } from "./api";
import { parseDays, WINDOWS, type StatsFilters } from "./format";

/**
 * The Performance filters, kept in the URL so a filtered view is a link. The
 * controls are the shell-owned `FilterBar` (I-7); this module draws none.
 */
export const WINDOW_SPEC: FilterSpec = {
  name: "days",
  label: "Window",
  kind: "choice",
  options: WINDOWS.map((value) => ({ value, label: `Last ${value} days` })),
};

/**
 * DEBT-1: CVCE records no operator on a marketing asset, so there is nothing
 * to filter by. The control stays on screen, inert, with the reason beside it,
 * because CX has four filters and a missing one would be a silent omission.
 */
export const OPERATOR_GAP = "No operator is recorded on marketing assets yet, so Operator cannot filter (DEBT-1).";

export const operatorFilter: FilterSpec = {
  name: "operator",
  label: "Operator",
  kind: "choice",
  options: [],
  anyLabel: "All",
  disabled: true,
};

export type Controls = StatsFilters & {
  cohort: string;
  values: FilterValues;
  onChange: (next: FilterValues) => void;
};

export function useControls(): Controls {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const values = parseFilters(search);
  const text = (key: string) => (typeof values[key] === "string" && values[key] ? (values[key] as string) : null);
  return {
    days: parseDays(values.days),
    run: text("run"),
    medium: text("medium"),
    cohort: text("cohort") ?? "snippet",
    values,
    onChange: (next) => {
      void navigate({ to: ".", search: applyFilters(search, next), replace: true });
    },
  };
}

export function PerformanceFilters({
  controls,
  runs,
  formats,
}: {
  controls: Controls;
  runs: RunOption[] | null;
  formats: FormatOption[] | null;
}) {
  const mediums = [...new Set((formats ?? []).map((f) => f.content_model).filter((m): m is string => Boolean(m)))].sort();
  const filters: FilterSpec[] = [
    WINDOW_SPEC,
    operatorFilter,
    {
      name: "run",
      label: "Campaign / Run",
      kind: "choice",
      anyLabel: "All",
      disabled: runs === null,
      options: (runs ?? []).map((r) => ({ value: r.marketing_run_id, label: r.marketing_run_id })),
    },
    {
      name: "medium",
      label: "Medium / Platform",
      kind: "choice",
      anyLabel: "All",
      disabled: formats === null,
      options: mediums.map((m) => ({ value: m, label: m })),
    },
  ];
  return (
    <div className="flex flex-col gap-1">
      <FilterBar
        layout="inline"
        filters={filters}
        values={{ ...controls.values, days: String(controls.days) }}
        onChange={controls.onChange}
      />
      <p className="text-sm text-ks-ink">{OPERATOR_GAP}</p>
      {(runs === null || formats === null) && (
        <p className="text-sm text-ks-ink">Some filter options could not be loaded from CVCE telemetry, so those filters are inert.</p>
      )}
    </div>
  );
}
